<template>
  <div
    class="base-island"
    :class="computedClass"
    :style="props.backgroundImage ? { backgroundImage: `url(${props.backgroundImage})` } : undefined"
  >
    <slot />
    <div v-if="false" class="base-island__bg-decor" />
  </div>
</template>

<script setup lang="ts">
interface Props {
  size?: 'sm' | 'sm-alt' | 'md' | 'lg' | 'xxl'
  variant?: 'light' | 'secondary' | 'transparent-inverted-200' | 'light-transparent-600'
  backgroundImage?: string
  withShadow?: boolean
  withFooterBg?: boolean
  withFullHeight?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'light',
  withShadow: true,
  size: 'md',
  withFooterBg: false,
})

const computedClass = computed(() => ({
  [`base-island--size-${props.size}`]: true,
  [`base-island--variant-${props.variant}`]: true,
  'base-island--with-shadow': props.withShadow,
  'base-island--with-footer-bg': false,
  'base-island--with-full-height': props.withFullHeight,
  'base-island--with-background-image': props.backgroundImage,
}))
</script>

<style lang="scss" scoped>
.base-island {
  position: relative;

  &--with-footer-bg {
    padding-bottom: rem(100px) !important;

    @include bp-media('xs') {
      padding-bottom: rem(80px) !important;
    }
  }

  &--with-full-height {
    min-height: 100%;
    display: flex;
    flex-direction: column;

    & > * {
      flex: 1;
    }
  }

  &--size {
    &-sm {
      padding: rem(8px);
      border-radius: var(--radius-sm);
    }

    &-sm-alt {
      padding: rem(16px);
      border-radius: calc(var(--radius-sm) + 4px);
    }

    &-md {
      padding: rem(20px);
      border-radius: var(--radius-md);

      @include bp-media('xs', 'sm') {
        padding: rem(16px);
        border-radius: calc(var(--radius-md) - 10px);
      }
    }

    &-lg {
      padding: rem(32px);
      border-radius: var(--radius-md);

      @include bp-media('xs', 'sm') {
        padding: rem(24px);
      }

      @include bp-media('xs') {
        padding: rem(16px);
        border-radius: calc(var(--radius-md) - 10px);
      }
    }

    &-xxl {
      padding: rem(72px) rem(60px);
      border-radius: var(--radius-lg);

      @include bp-media('xs', 'sm', 'md') {
        padding: rem(56px) rem(44px);
      }

      @include bp-media('xs') {
        padding: rem(40px) rem(16px);
      }
    }
  }

  &--variant {
    &-light {
      background-color: var(--color-surface-bg);

      &-transparent-600 {
        background-color: var(--color-surface-bg-transparent-600);
        backdrop-filter: blur(7px);
      }
    }

    &-secondary {
      background-color: var(--color-surface-placeholder);
    }

    &-transparent-inverted-200 {
      background-color: var(--color-surface-transparent-inverted-200);
    }
  }

  &--with-shadow {
    box-shadow: 0 0 12px rgb(51 51 51 / 20%);

    &.base-island--size-lg,
    &.base-island--size-xxl {
      box-shadow: 0 0 13.8px #00000040;
    }
  }

  &__bg-decor {
    position: absolute;
    background-image: url("/user-header-bg.webp");
    background-size: cover;
    background-repeat: no-repeat;
    border-radius: 0 0 var(--radius-md) var(--radius-md);
    left: 0;
    bottom: 0;
    width: 100%;
    height: rem(100px);

    @include bp-media('xs') {
      height: rem(80px);
      border-radius: 0 0 calc(var(--radius-md) - 10px) calc(var(--radius-md) - 10px);
    }
  }

  &--with-background-image {
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
  }
}
</style>
